import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { brands, companyInfo } from '../data/mockData';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Download, ArrowLeft, Beaker, Send, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent } from '../analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BrandProducts = () => {
  const { brandSlug } = useParams();
  const brand = brands.find(b => b.slug === brandSlug);

  const [customFormData, setCustomFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    application: '',
    requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!brand) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Brand Not Found</h1>
          <Link to="/">
            <Button>Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleDownloadTDS = (product) => {
    if (product.tdsUrl) {
      window.open(product.tdsUrl, '_blank');
    }
  };

  const handleEnquireNow = (productName) => {
    const message = `Hi, I'm interested in ${brand.name} ${productName}. Please provide more information.`;
    window.open(`${companyInfo.whatsappLink}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleCustomFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/inquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          inquiry_type: 'custom_formulation',
          name: customFormData.name,
          email: customFormData.email,
          company: customFormData.company,
          phone: customFormData.phone,
          brand_name: brand.name,
          message: `Application: ${customFormData.application}\n\nRequirements: ${customFormData.requirements}`
        }),
      });

      if (response.ok) {
        toast.success('Thank you! Our technical team will contact you shortly regarding custom formulations.');
        trackEvent('form_submit', { form_type: 'custom_formulation', brand: brand.name });
        setCustomFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          application: '',
          requirements: ''
        });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasProducts = brand.products && brand.products.length > 0;
  const hasCustomFormulation = brand.customFormulation && brand.customFormulation.enabled;
  const isWhiteBanner = brand.slug === 'titax' || brand.slug === 'albalux' || brand.slug === 'tinergy';

  return (
    <div className="min-h-screen">
      <SEO
        title={`${brand.name} — TiO2 & Specialty Pigments`}
        description={`Explore ${brand.name} products from Trustar Industrial Chemicals. ${brand.description || 'Premium TiO2 and specialty pigment grades for coatings, plastics, and industrial applications.'}`}
        path={`/products/${brandSlug}`}
      />
      {/* Brand Hero - White banner for Titax & Albalux, Gradient for Tinergy */}
      {isWhiteBanner ? (
        <section className="bg-white py-20 border-b-2 border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/" className="inline-flex items-center text-gray-600 hover:text-primary mb-8 transition-colors">
              <ArrowLeft size={20} className="mr-2" />
              Back to Home
            </Link>
            
            <div className="text-center">
              {/* Brand Logo - Larger and highlighted */}
              <div className="mb-8">
                {brand.logoUrl ? (
                  <div className="inline-block">
                    <img 
                      src={brand.logoUrl} 
                      alt={brand.name}
                      className="h-24 mx-auto object-contain"
                    />
                  </div>
                ) : (
                  <h1 className="text-6xl font-bold">{brand.name}</h1>
                )}
              </div>
              
              <p className="text-2xl text-gray-700 font-light mb-4">{brand.tagline}</p>
              <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
                {brand.description}
              </p>
            </div>
          </div>
        </section>
      ) : (
        <section className={`relative min-h-[500px] bg-gradient-to-br ${brand.gradient} text-white overflow-hidden`}>
          {/* Premium Abstract Pattern Overlay */}
          <div className="absolute inset-0 opacity-15">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 25% 50%, rgba(255,255,255,0.2) 0%, transparent 50%),
                               radial-gradient(circle at 75% 70%, rgba(255,255,255,0.15) 0%, transparent 50%)`
            }}></div>
          </div>
          
          {/* Geometric pattern */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `linear-gradient(30deg, rgba(255,255,255,0.1) 12%, transparent 12.5%, transparent 87%, rgba(255,255,255,0.1) 87.5%)`,
            backgroundSize: '80px 140px'
          }}></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-8 transition-colors">
              <ArrowLeft size={20} className="mr-2" />
              Back to Home
            </Link>
            
            <div className="max-w-4xl">
              {/* Brand Logo */}
              <div className="mb-8">
                {brand.logoUrl ? (
                  <div className="bg-white rounded-2xl p-6 inline-block shadow-lg">
                    <img 
                      src={brand.logoUrl} 
                      alt={brand.name}
                      className="h-16 max-w-xs object-contain"
                    />
                  </div>
                ) : (
                  <div className="h-20 w-64 bg-white/10 backdrop-blur-md rounded-lg flex items-center justify-center">
                    <span className="text-4xl font-bold">{brand.name}</span>
                  </div>
                )}
              </div>
              
              <p className="text-2xl text-white/95 font-light mb-6">{brand.tagline}</p>
              <p className="text-lg text-white/85 leading-relaxed max-w-3xl">
                {brand.description}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Brand Info */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div>
              <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Region</p>
              <p className="text-lg font-semibold text-gray-900">{brand.region}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Manufacturer</p>
              <p className="text-lg font-semibold text-gray-900">{brand.manufacturer}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Products</p>
              <p className="text-lg font-semibold text-gray-900">
                {hasProducts ? brand.products.length : 'Coming Soon'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* For Albalux - Balanced Custom Formulation and Standard Products */}
      {hasCustomFormulation && (
        <>
          {/* Standard Products First - Equal Prominence */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4">Standard Product Range</h2>
                <p className="text-xl text-gray-600">
                  High-quality specialty pigments for immediate delivery
                </p>
              </div>

              {hasProducts && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                  {brand.products.slice(0, 3).map((product) => (
                    <Card key={product.id} className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                      <CardContent className="p-0">
                        <div className={`relative h-56 bg-gradient-to-br ${brand.gradient} flex items-center justify-center overflow-hidden`}>
                          <div className="absolute inset-0 opacity-20" style={{
                            backgroundImage: `radial-gradient(circle at 30% 40%, rgba(255,255,255,0.3) 0%, transparent 50%)`
                          }}></div>
                          <div className="text-center z-10">
                            <div className="bg-white/20 backdrop-blur-md px-8 py-6 rounded-2xl border border-white/30">
                              <span className="text-6xl font-bold text-white drop-shadow-lg">{product.name}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="p-6">
                          <div className="mb-3">
                            <h3 className="text-2xl font-bold text-primary">{product.name}</h3>
                            <p className="text-sm text-gray-500">{product.grade}</p>
                          </div>
                          <p className="text-sm font-medium text-gray-600 mb-3">{product.category}</p>
                          <p className="text-gray-700 mb-4 leading-relaxed text-sm line-clamp-3">
                            {product.description}
                          </p>

                          <div className="flex gap-2">
                            {product.tdsUrl && (
                              <Button
                                onClick={() => handleDownloadTDS(product)}
                                variant="outline"
                                className="flex-1"
                                size="sm"
                              >
                                <Download size={16} className="mr-2" />
                                TDS
                              </Button>
                            )}
                            <Button
                              onClick={() => handleEnquireNow(product.name)}
                              className="flex-1 bg-green-600 hover:bg-green-700"
                              size="sm"
                            >
                              <MessageCircle size={16} className="mr-2" />
                              Enquire
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Custom Formulation Section - Equal Prominence */}
          <section className="py-20 bg-gradient-to-br from-purple-50 to-indigo-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4">{brand.customFormulation.headline}</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {brand.customFormulation.description}
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-12 items-start">
                <div>
                  <Card className="border-none shadow-xl">
                    <CardContent className="p-8">
                      <h3 className="text-2xl font-bold mb-6">Request Custom Formulation</h3>
                      <form onSubmit={handleCustomFormSubmit} className="space-y-4">
                        <div>
                          <Label htmlFor="name">Name *</Label>
                          <Input
                            id="name"
                            required
                            value={customFormData.name}
                            onChange={(e) => setCustomFormData({...customFormData, name: e.target.value})}
                            placeholder="Your name"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            type="email"
                            required
                            value={customFormData.email}
                            onChange={(e) => setCustomFormData({...customFormData, email: e.target.value})}
                            placeholder="your@email.com"
                            className="mt-1"
                          />
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="company">Company</Label>
                            <Input
                              id="company"
                              value={customFormData.company}
                              onChange={(e) => setCustomFormData({...customFormData, company: e.target.value})}
                              placeholder="Company name"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="phone">Phone</Label>
                            <Input
                              id="phone"
                              value={customFormData.phone}
                              onChange={(e) => setCustomFormData({...customFormData, phone: e.target.value})}
                              placeholder="+971 XX XXX XXXX"
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="application">Application Type *</Label>
                          <Input
                            id="application"
                            required
                            value={customFormData.application}
                            onChange={(e) => setCustomFormData({...customFormData, application: e.target.value})}
                            placeholder="e.g., Automotive coatings, specialty inks"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="requirements">Your Requirements *</Label>
                          <Textarea
                            id="requirements"
                            required
                            value={customFormData.requirements}
                            onChange={(e) => setCustomFormData({...customFormData, requirements: e.target.value})}
                            placeholder="Please describe your specific formulation needs..."
                            rows={5}
                            className="mt-1"
                          />
                        </div>
                        <Button type="submit" size="lg" className="w-full" disabled={isSubmitting} data-testid="custom-form-submit">
                          {isSubmitting ? (
                            <>Submitting...</>
                          ) : (
                            <>
                              <Send size={18} className="mr-2" />
                              Submit Inquiry
                            </>
                          )}
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <div className="bg-white rounded-2xl p-8 shadow-lg mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-2xl mb-6">
                      <Beaker className="text-purple-600" size={32} />
                    </div>
                    <h3 className="text-2xl font-bold mb-4">Why Custom Formulation?</h3>
                    <ul className="space-y-4">
                      {[
                        'Bespoke pigment formulations',
                        'Application-specific solutions',
                        'Performance optimization',
                        'Technical consultation included',
                        'Quality assurance testing',
                        'Flexible order quantities'
                      ].map((item, index) => (
                        <li key={index} className="flex items-start">
                          <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3"></div>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-purple-600 text-white rounded-2xl p-8 shadow-lg">
                    <h3 className="text-xl font-bold mb-3">Quick Response Time</h3>
                    <p className="text-purple-100">
                      Our technical team will contact you within 24 hours to discuss your 
                      custom formulation requirements and provide expert guidance.
                    </p>
                  </div>
                </div>
              </div>

              {/* Remaining Standard Products */}
              {hasProducts && brand.products.length > 3 && (
                <div className="mt-16">
                  <h3 className="text-3xl font-bold text-center mb-8">More Standard Products</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {brand.products.slice(3).map((product) => (
                      <Card key={product.id} className="border-none shadow-xl hover:shadow-2xl transition-all duration-300">
                        <CardContent className="p-0">
                          <div className={`relative h-56 bg-gradient-to-br ${brand.gradient} flex items-center justify-center`}>
                            <div className="text-center z-10">
                              <div className="bg-white/20 backdrop-blur-md px-8 py-6 rounded-2xl border border-white/30">
                                <span className="text-6xl font-bold text-white drop-shadow-lg">{product.name}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="p-6">
                            <div className="mb-3">
                              <h3 className="text-2xl font-bold text-primary">{product.name}</h3>
                              <p className="text-sm text-gray-500">{product.grade}</p>
                            </div>
                            <p className="text-sm font-medium text-gray-600 mb-3">{product.category}</p>
                            <p className="text-gray-700 mb-4 leading-relaxed text-sm line-clamp-3">
                              {product.description}
                            </p>

                            <div className="flex gap-2">
                              {product.tdsUrl && (
                                <Button
                                  onClick={() => handleDownloadTDS(product)}
                                  variant="outline"
                                  className="flex-1"
                                  size="sm"
                                >
                                  <Download size={16} className="mr-2" />
                                  TDS
                                </Button>
                              )}
                              <Button
                                onClick={() => handleEnquireNow(product.name)}
                                className="flex-1 bg-green-600 hover:bg-green-700"
                                size="sm"
                              >
                                <MessageCircle size={16} className="mr-2" />
                                Enquire
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* Products Section for non-custom brands (Titax, Tinergy) */}
      {!hasCustomFormulation && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12">
              <h2 className="text-4xl font-bold mb-4">Product Range</h2>
              <p className="text-xl text-gray-600">
                Premium titanium dioxide and specialty pigments
              </p>
            </div>

            {hasProducts ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {brand.products.map((product) => (
                  <Card key={product.id} className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                    <CardContent className="p-0">
                      {/* Product Grade Display */}
                      <div className={`relative h-56 bg-gradient-to-br ${brand.gradient} flex items-center justify-center overflow-hidden`}>
                        <div className="absolute inset-0 opacity-20" style={{
                          backgroundImage: `radial-gradient(circle at 30% 40%, rgba(255,255,255,0.3) 0%, transparent 50%)`
                        }}></div>
                        
                        <div className="text-center z-10">
                          <div className="bg-white/20 backdrop-blur-md px-8 py-6 rounded-2xl border border-white/30">
                            <span className="text-6xl font-bold text-white drop-shadow-lg">{product.name}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-2xl font-bold text-primary">{product.name}</h3>
                            <p className="text-sm text-gray-500">{product.grade} Grade</p>
                          </div>
                        </div>

                        <p className="text-sm font-medium text-gray-600 mb-3">{product.category}</p>
                        
                        <p className="text-gray-700 mb-4 leading-relaxed text-sm line-clamp-3">
                          {product.description}
                        </p>

                        {/* Applications */}
                        {product.applications && product.applications.length > 0 && (
                          <div className="mb-4">
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Applications</p>
                            <div className="flex flex-wrap gap-2">
                              {product.applications.slice(0, 2).map((app, idx) => (
                                <span
                                  key={idx}
                                  className="text-xs bg-gray-100 px-3 py-1 rounded-full"
                                >
                                  {app}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* CTA Buttons */}
                        <div className="flex gap-2">
                          {product.tdsUrl && (
                            <Button
                              onClick={() => handleDownloadTDS(product)}
                              variant="outline"
                              className="flex-1"
                              size="sm"
                            >
                              <Download size={16} className="mr-2" />
                              Download TDS
                            </Button>
                          )}
                          <Button
                            onClick={() => handleEnquireNow(product.name)}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            size="sm"
                          >
                            <MessageCircle size={16} className="mr-2" />
                            Enquire Now
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-gray-200 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <Beaker className="text-gray-400" size={40} />
                </div>
                <h3 className="text-2xl font-bold mb-2">Products Coming Soon</h3>
                <p className="text-gray-600 mb-8">
                  We're updating our product catalog. Please contact us for product information.
                </p>
                <Link to="/contact">
                  <Button size="lg">Contact Us</Button>
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Technical Support?</h2>
          <p className="text-lg text-gray-700 mb-8">
            Our team can help you select the optimal product for your specific application requirements.
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-primary hover:bg-primary/90">
                Request A Quote
              </Button>
            </Link>
            <Button
              size="lg"
              variant="outline"
              onClick={() => window.open(companyInfo.whatsappLink, '_blank')}
              className="border-green-600 text-green-600 hover:bg-green-50"
            >
              <MessageCircle size={18} className="mr-2" />
              Chat on WhatsApp
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BrandProducts;
